import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, tokens, clearOfflineData } from "@/lib/api";
import { syncQueue } from "@/lib/offlineSync";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const sync = async () => {
      const n = await syncQueue();
      if (n) {
        toast.success(`Synced ${n} offline activit${n > 1 ? "ies" : "y"}`);
        refreshUser();
      }
    };
    refreshUser().then((u) => u && sync());
    window.addEventListener("online", sync);
    return () => window.removeEventListener("online", sync);
  }, [refreshUser]);

  const authenticate = async (mode, payload) => {
    const { data } = await api.post(`/auth/${mode}`, payload);
    tokens.set(data);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    await clearOfflineData();
    tokens.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, refreshUser, authenticate, logout }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
